import type { Option } from "@inrev/core";
import { useCallback, useState } from "react";
import type { PDFViewerControl } from "./documentSource";
import { useDocumentDownload } from "./useDocumentDownload";
import type { DocumentViewerController } from "./useDocumentViewer";

/** Download and print, with one error line for whichever of them last failed. */
export const useViewerActions = (
	control: Option<PDFViewerControl>,
	printDocument: DocumentViewerController["print"],
) => {
	const downloader = useDocumentDownload(control);
	const [printFailed, setPrintFailed] = useState(false);
	const [printing, setPrinting] = useState(false);
	const { download: startDownload, dismissError: dismissDownloadError } = downloader;

	const download = useCallback(() => {
		setPrintFailed(false);
		startDownload();
	}, [startDownload]);

	const print = useCallback(() => {
		dismissDownloadError();
		setPrintFailed(false);
		setPrinting(true);
		printDocument()
			.catch(() => setPrintFailed(true))
			.finally(() => setPrinting(false));
	}, [printDocument, dismissDownloadError]);

	const dismissError = useCallback(() => {
		dismissDownloadError();
		setPrintFailed(false);
	}, [dismissDownloadError]);

	const error = downloader.failed
		? "The download didn't start. Try again."
		: printFailed
			? "The document couldn't be printed. Try again."
			: undefined;

	return {
		download,
		print,
		canDownload: downloader.canDownload,
		downloading: downloader.downloading,
		printing,
		error,
		dismissError,
	};
};
